import React, { Suspense } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PresenceProvider } from './contexts/PresenceContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { isMobileViewport, MOBILE_BREAKPOINT_PX } from './hooks/useIsMobile';
import { ConfirmProvider } from './contexts/ConfirmContext';
import { ImpersonationProvider } from './lib/impersonation';
import { ToastProvider } from './contexts/ToastContext';
import { useUsageTracking } from './hooks/useUsageTracking'; // TEMP: front-end usage study, remove after 2026-07-07
import { colors, focusRing } from './lib/styleTokens';
import { VIEW_AS } from './supabaseClient';
import ViewAsBanner, { VIEW_AS_BAR_HEIGHT } from './components/ViewAsBanner';

// Pick the layout + auth chunks once at boot. Cross-breakpoint resize requires reload.
// Reload when the viewport crosses the mobile breakpoint after boot
const _initialMobile = isMobileViewport();
if (typeof window !== 'undefined') {
  const _mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`);
  const _handler = (e) => { if (e.matches !== _initialMobile) window.location.reload(); };
  if (_mq.addEventListener) _mq.addEventListener('change', _handler);
  else _mq.addListener(_handler);
}

const Layout = isMobileViewport()
  ? React.lazy(() => import('./pages/AppLayoutMobile'))
  : React.lazy(() => import('./pages/AppLayout'));
const FlightlineDashboard = React.lazy(() => import('./pages/flightline/FlightlineDashboard'));
const MfaGate = React.lazy(() => import('./components/MfaGate'));

const AuthPage = isMobileViewport()
  ? React.lazy(() => import('./pages/AuthPageMobile'))
  : React.lazy(() => import('./pages/AuthPage'));

function AppContent() {
  const { user, profile, loading, signOut, isPasswordRecovery, isInviteSetup, mfaRequired } = useAuth();

  useUsageTracking(user, profile); // TEMP: front-end usage study, no-op outside production

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.loadingInner}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Loading...</p>
        </div>
      </div>
    );
  }

  // User is logged in but profile failed to load — offer sign out
  if (user && !profile) {
    return (
      <div style={styles.loading}>
        <div style={styles.loadingInner}>
          <p style={styles.errorText}>Having trouble loading your profile.</p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button onClick={() => window.location.reload()} style={styles.retryBtn}>
              Retry
            </button>
            <button onClick={async () => { await signOut(); window.location.reload(); }} style={{ ...styles.retryBtn, background: 'rgba(255,255,255,0.1)' }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // During password recovery or invite setup, always show AuthPage
  if (isPasswordRecovery || isInviteSetup) {
    return (
      <Suspense fallback={<LayoutFallback />}>
        <AuthPage />
      </Suspense>
    );
  }

  if (!(user && profile)) {
    return (
      <Suspense fallback={<LayoutFallback />}>
        <AuthPage />
      </Suspense>
    );
  }

  // Email two-factor: the session exists, but nothing renders until this
  // device is verified (or was remembered within the last 30 days).
  if (mfaRequired) {
    return (
      <Suspense fallback={<LayoutFallback />}>
        <MfaGate />
      </Suspense>
    );
  }

  // Flightline access is granted by its service, independent of suite admin access.
  // Keep this after login/recovery/profile checks so the existing Mayday login is reused.
  if (/^\/(flightline|radar)(\/|$)/.test(window.location.pathname)) {
    return <Suspense fallback={<LayoutFallback />}><FlightlineDashboard /></Suspense>;
  }

  // Signed in on /triton-sso → hand off to Triton instead of the layout.
  if (isTritonSsoPath()) {
    return (
      <Suspense fallback={<LayoutFallback />}>
        <TritonSso />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LayoutFallback />}>
      <Layout />
    </Suspense>
  );
}

function LayoutFallback() {
  return (
    <div style={styles.loading}>
      <div style={styles.loadingInner}>
        <div style={styles.spinner} />
      </div>
    </div>
  );
}

// Public careers board — served before the auth gate so visitors never log in.
const PublicCareers = React.lazy(() => import('./pages/public/PublicCareers'));
const PublicBrief = React.lazy(() => import('./pages/public/PublicBrief'));
const PublicDeliverables = React.lazy(() => import('./pages/public/PublicDeliverables'));
// Harbor guest join — tokenized, login-free call entry (guests never see staff
// chrome). Must be checked before the layouts ever see the 'harbor' segment.
const HarborJoin = React.lazy(() => import('./pages/harbor/HarborJoin'));
// Triton SSO hand-off — rendered inside the auth gate (AppContent), so an
// anonymous visitor logs in first and then continues to Triton.
const TritonSso = React.lazy(() => import('./pages/TritonSso'));
function isCareersPath() {
  return /^\/careers(\/|$)/.test(window.location.pathname);
}
function isBriefPath() {
  return /^\/brief\/[^/]+/.test(window.location.pathname);
}
function isDeliverablesPath() {
  return /^\/deliverables(\/|$)/.test(window.location.pathname);
}
function isHarborJoinPath() {
  return /^\/harbor\/join\/[^/]+/.test(window.location.pathname);
}
function isTritonSsoPath() {
  return /^\/triton-sso(\/|$)/.test(window.location.pathname);
}

export default function App() {
  if (isCareersPath()) {
    return (
      <Suspense fallback={<LayoutFallback />}>
        <PublicCareers />
      </Suspense>
    );
  }
  if (isBriefPath()) {
    return (
      <Suspense fallback={<LayoutFallback />}>
        <PublicBrief />
      </Suspense>
    );
  }
  if (isDeliverablesPath()) {
    return (
      <Suspense fallback={<LayoutFallback />}>
        <PublicDeliverables />
      </Suspense>
    );
  }
  if (isHarborJoinPath()) {
    return (
      <Suspense fallback={<LayoutFallback />}>
        <HarborJoin />
      </Suspense>
    );
  }
  const tree = (
    <AuthProvider>
      <PresenceProvider>
        <NotificationProvider>
          <ConfirmProvider>
            <ToastProvider>
              <ImpersonationProvider>
                <AppContent />
              </ImpersonationProvider>
            </ToastProvider>
          </ConfirmProvider>
        </NotificationProvider>
      </PresenceProvider>
    </AuthProvider>
  );

  // A "View as…" tab runs the entire app under the target's session (see
  // lib/viewAs.js), so the whole tree gets pushed down under a standing banner.
  if (!VIEW_AS.active) return tree;
  return (
    <>
      <ViewAsBanner />
      <div style={{ paddingTop: `${VIEW_AS_BAR_HEIGHT}px` }}>{tree}</div>
    </>
  );
}

const styles = {
  loading: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: colors.bg,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  loadingInner: {
    textAlign: 'center',
  },
  spinner: {
    width: '36px',
    height: '36px',
    border: '3px solid rgba(187,68,48,0.2)',
    borderTopColor: '#bb4430',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 16px',
  },
  loadingText: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '14px',
  },
  errorText: {
    color: '#f4a3a5',
    fontSize: '14px',
    marginBottom: '16px',
  },
  retryBtn: {
    padding: '10px 24px',
    background: '#bb4430',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
};

// Add spinner animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    background: #0f0d0e;
    color: #ece7e2;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-feature-settings: 'cv11', 'ss01', 'ss03';
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }
  button, input, select, textarea { font-family: inherit; }
  ::selection { background: rgba(187,68,48,0.35); }
  ::-webkit-scrollbar { width: 8px; height: 8px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 4px; border: 2px solid transparent; background-clip: padding-box; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.16); background-clip: padding-box; }
  select option { background: #211d1e; color: #ece7e2; }
  input::placeholder { color: rgba(255,255,255,0.25); }
  input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(0.5); }
  /* Keyboard focus visibility (a11y). !important overrides the many inline
     outline:'none' declarations. :focus-visible => keyboard nav only, so
     mouse clicks stay ring-free. */
  *:focus-visible { outline: ${focusRing.outline} !important; outline-offset: ${focusRing.offset} !important; }
  *:focus:not(:focus-visible) { outline: none; }
`;
document.head.appendChild(styleSheet);
